import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, MessageCircle, Award, Stethoscope, GraduationCap, BookOpen, ShieldCheck, Star, ArrowRight, MapPin } from "lucide-react";
import { CLINIC, CREDENTIALS, STATS, CONDITIONS, FAQS, CITY_PAGES } from "@/data/clinic";
import AppointmentForm from "@/components/AppointmentForm";
import { api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead, { physicianSchema, localBusinessSchema, reviewAggregateSchema } from "@/components/SEOHead";

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data || [])).catch(() => {});
    api.get("/blogs").then((r) => setBlogs(r.data || [])).catch(() => {});
  }, []);

  const schemas = [
    { id: "ld-physician", data: physicianSchema() },
    { id: "ld-localbiz", data: localBusinessSchema() },
  ];
  const reviewS = reviewAggregateSchema(testimonials);
  if (reviewS) schemas.push({ id: "ld-reviews", data: reviewS });

  return (
    <div data-testid="home-page">
      <SEOHead
        title="Dr. Ashutosh Mishra | Best Diabetologist & Endocrinologist in Varanasi"
        description="Dr. Ashutosh Mishra — Senior Diabetologist & Endocrinologist in Varanasi with 20+ years of experience. MBBS, MD (IMS BHU), DFID (CMC Vellore), DMSc Endocrinology (UK). Book appointment."
        path="/"
        schemas={schemas}
        faqs={FAQS.slice(0, 6)}
      />

      {/* Hero */}
      <section className="hero-grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3.5 py-1.5 rounded-full text-xs font-medium tracking-wide">
              <ShieldCheck className="w-3.5 h-3.5" /> Senior Specialist • 20+ Years • Kabir Nagar, Varanasi
            </div>
            <h1 className="mt-5 font-poppins font-semibold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight">
              <span className="text-green-700">Diabetologist</span> &<br />
              <span className="text-slate-900">Endocrinologist</span> in Varanasi
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              {CLINIC.doctorName} — MBBS, MD (IMS BHU), DFID (CMC Vellore), DMSc Endocrinology (UK). Comprehensive care for diabetes, thyroid, PCOS, obesity, hormonal disorders and preventive nephrology.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-book-btn" className="inline-flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white rounded-full px-7 py-3.5 font-medium text-sm shadow-md shadow-green-700/20 transition-all">
                Book Appointment <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:+91${CLINIC.phone}`} data-testid="hero-call-btn" className="inline-flex items-center gap-2 border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-7 py-3 font-medium text-sm transition-all">
                <Phone className="w-4 h-4" /> Call {CLINIC.phoneDisplay}
              </a>
              <a href={`https://wa.me/${CLINIC.whatsapp}`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-emerald-500 text-white rounded-full px-6 py-3 font-medium text-sm transition-all">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-poppins font-semibold text-3xl sm:text-4xl text-slate-900">{s.value}</div>
                  <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-up">
            <div className="absolute -top-6 -left-6 w-44 h-44 rounded-full bg-green-100/60 blur-2xl" />
            <div className="absolute -bottom-8 -right-6 w-52 h-52 rounded-full bg-green-100/40 blur-2xl" />
            <div className="relative bg-white rounded-[2rem] border border-slate-100 shadow-xl shadow-green-900/10 overflow-hidden">
              <img src={CLINIC.doctorPhoto} alt={`${CLINIC.doctorName} — Diabetologist & Endocrinologist in Varanasi`} className="w-full h-[520px] object-cover" data-testid="hero-doctor-photo" />
              <div className="p-5 border-t border-slate-100 bg-white">
                <div className="font-poppins font-semibold text-xl text-slate-900">{CLINIC.doctorName}</div>
                <div className="text-xs text-slate-500 mt-1">{CLINIC.fullTitle}</div>
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                  <MapPin className="w-3.5 h-3.5 text-green-700" /> {CLINIC.shortAddress}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials strip */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap justify-center items-center gap-x-10 gap-y-3 text-sm">
          {CREDENTIALS.map((c) => (
            <div key={c.label} className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-green-400" />
              <span className="text-slate-100">{c.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* City pages CTA */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {CITY_PAGES.map((p) => (
              <Link key={p.slug} to={`/${p.slug}`} data-testid={`city-card-${p.slug}`} className="group bg-white border border-slate-100 rounded-3xl p-7 hover:shadow-md hover:-translate-y-0.5 transition-all flex gap-5 items-center">
                <img src={p.image} alt={p.title} className="w-24 h-24 object-cover rounded-2xl" />
                <div>
                  <h2 className="font-poppins font-semibold text-2xl text-slate-900">{p.title}</h2>
                  <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{p.intro[0]}</p>
                  <div className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-green-700 group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-green-700 font-medium">What we treat</div>
            <h2 className="font-poppins font-semibold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-2">Conditions & Treatments in Varanasi</h2>
            <p className="text-slate-600 mt-4 text-base leading-relaxed">
              Comprehensive specialist care across diabetes, endocrinology and metabolic medicine — under one roof at Kabir Nagar, Varanasi.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CONDITIONS.map((c) => (
              <Link
                key={c.slug}
                to={`/${c.slug}`}
                data-testid={`condition-card-${c.slug}`}
                className="group bg-white rounded-3xl border border-slate-100 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-poppins font-semibold text-xl text-slate-900">{c.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-2">{c.short}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-green-700 group-hover:gap-3 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="bg-green-50/40 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-green-700 font-medium">Why Choose Dr. Ashutosh Mishra</div>
            <h2 className="font-poppins font-semibold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-2">Two decades of trusted, evidence-based care</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Trained at India's top medical institutions and the University of South Wales (UK), with an integrated approach to diabetes, hormones, weight and kidney health.
            </p>

            <div className="mt-8 space-y-5">
              {[
                { icon: GraduationCap, title: "Elite training", text: "MBBS, MD (IMS BHU), DFID (CMC Vellore), DMSc Endocrinology UK." },
                { icon: Stethoscope, title: "Integrated care", text: "One specialist for diabetes, thyroid, PCOS, obesity, hormones and kidney protection." },
                { icon: Award, title: "Patient-first approach", text: "Personalised diet and medication plans, not one-size-fits-all prescriptions." },
                { icon: BookOpen, title: "Academic backbone", text: "Active in research, teaching and modern protocols (CGM, GLP-1, SGLT2)." },
              ].map((f) => (
                <div key={f.title} className="flex gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-2xl bg-white grid place-items-center text-green-700 border border-green-100 shadow-sm">
                    <f.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 font-poppins text-base">{f.title}</h3>
                    <div className="text-sm text-slate-600 mt-1">{f.text}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-7">
              <Link to="/about-dr-ashutosh-mishra" className="text-sm font-medium text-green-700 inline-flex items-center gap-1">More about Dr. Mishra <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>

          <div className="lg:pl-6">
            <AppointmentForm />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-green-700 font-medium">Patient stories</div>
            <h2 className="font-poppins font-semibold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-2">Real Varanasi patients. Real outcomes.</h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((t) => (
              <div key={t.id} data-testid={`testimonial-${t.id}`} className="bg-white border border-slate-100 rounded-3xl p-7 hover:shadow-md transition">
                <div className="flex gap-0.5 text-amber-500 mb-3">
                  {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-500" />)}
                </div>
                <p className="text-slate-700 text-[15px] leading-relaxed">"{t.quote}"</p>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <div className="font-medium text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.condition} • {t.location}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/patient-testimonials" className="text-sm font-medium text-green-700 inline-flex items-center gap-1">Read more testimonials <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Blogs preview */}
      <section className="bg-slate-50 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-green-700 font-medium">Health insights</div>
              <h2 className="font-poppins font-semibold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-2">From Dr. Mishra's notebook</h2>
            </div>
            <Link to="/blog" data-testid="view-all-blogs" className="text-sm font-medium text-green-700 hover:gap-3 inline-flex items-center gap-1.5 transition-all">
              View all articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.slice(0, 3).map((b) => (
              <Link key={b.id} to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group bg-white rounded-3xl border border-slate-100 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="text-xs text-green-700 uppercase tracking-wider">{b.category}</div>
                  <h3 className="font-poppins font-semibold text-lg text-slate-900 mt-2 leading-snug">{b.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 line-clamp-3">{b.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.22em] text-green-700 font-medium">Common questions</div>
            <h2 className="font-poppins font-semibold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-2">Frequently asked questions</h2>
          </div>
          <Accordion type="single" collapsible className="mt-12" data-testid="home-faq-accordion">
            {FAQS.slice(0, 6).map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-slate-200">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-medium text-slate-900 hover:text-green-700">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-10 text-center">
            <Link to="/faqs" className="text-sm font-medium text-green-700 inline-flex items-center gap-1.5">
              See all FAQs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact band */}
      <section className="bg-slate-950 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-poppins font-semibold text-3xl lg:text-4xl text-white">Visit the clinic in Varanasi</h2>
            <p className="text-slate-300 mt-3">{CLINIC.address}</p>
            <div className="mt-6 space-y-2 text-sm">
              {CLINIC.hours.map((h) => (
                <div key={h.day}><span className="text-green-300 font-medium">{h.day}:</span> {h.time}</div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`tel:+91${CLINIC.phone}`} className="bg-green-500 hover:bg-green-400 text-slate-900 rounded-full px-6 py-3 font-medium text-sm">
                Call {CLINIC.phoneDisplay}
              </a>
              <Link to="/contact" className="border border-white/30 hover:bg-white/10 rounded-full px-6 py-3 font-medium text-sm">Get directions</Link>
              <a href={CLINIC.mapLink} target="_blank" rel="noreferrer" className="border border-white/30 hover:bg-white/10 rounded-full px-6 py-3 font-medium text-sm">Open in Google Maps</a>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden border border-white/10">
            <iframe
              title="clinic-location"
              src={CLINIC.mapEmbed}
              width="100%"
              height="320"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
